import { lazy, Suspense, useContext, useEffect, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ROUTES from './Routes';

import { FileContextParentType, FileContextProvider } from '@/FileContext';
import {
  getFsmaNumbers,
  getPolicies,
  getTags,
  getTeamIntegrations,
  getUserTeamSettings,
} from '@/redux/actions';
import { AppContext } from '@/shared/context/context';
import { useUserRole } from '@/queries';
import useViewport from '@/shared/hooks/useViewport';

import BuildingContextProvider from '@/containers/Buildings/context/BuildingContextProvider';
import CallbackFront from '../../Callback/CallbackFront';
import Dashboard from '@/containers/Home/Dashboard';
import HomeownerAssociationContextProvider from '@/containers/HomeownerAssociation/context/HomeownerAssociationContextProvider';
import { InlineSpinner } from '@/shared/icons/Icons';
import Meta from '@/shared/components/Meta';
import OverViewSkeleton from '@/shared/components/2.0/skeletons/OverviewSkeleton';

const SyndicManagementOverview = lazy(() =>
  import('@/containers/SyndicManagement/SyndicManagementOverview'),
);

const BlogOverview = lazy(() => import('@/containers/Blogs/BlogOverview'));
const FsmaNbrOverview = lazy(() => import('@/containers/FsmaNbr/FsmaNbrOverview'));
const IntegratorsOverview = lazy(() =>
  import('@/containers/Administration/integrator/IntegratorsOverview'),
);
const KboPublicSearchBalance = lazy(() =>
  import('@/containers/Administration/KboPublicSearchBalance'),
);
const SmsOverview = lazy(() => import('@/containers/Sms/SmsOverview'));
const UnusedOctopusDossiers = lazy(() =>
  import('@/containers/Administration/UnusedOctopusDossiers'),
);

const ContactDetails = lazy(() => import('@/containers/Contacts/Details/ContactDetails'));
const ContactCommunicationOverview = lazy(() =>
  import('@/containers/Contacts/Details/communication/ContactCommunicationOverview'),
);

const CompanyDetails = lazy(() => import('@/containers/Companies/Details/CompanyDetails'));
const CompanyCommunicationOverview = lazy(() =>
  import('@/containers/Companies/Details/communication/CompanyCommunicationOverview'),
);

const CalendarsOverview = lazy(() => import('@/containers/Calendars/CalendarsOverview'));
const CommonTeamSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Common/CommonSettings'),
);
const AutomationSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Automations/AutomationSettings'),
);
const AiEmailDraftingSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Communication/AiEmailDrafting/AiEmailDraftingSettings'),
);
const BuildingPortalSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Building/BuildingSettings'),
);
const CaseEmailsOverview = lazy(() => import('@/containers/Cases/Email/CaseEmailsOverview'));
const CaseSettings = lazy(() => import('@/containers/Teams/TeamSettings/Case/CaseSettings'));
const CommunicationOverview = lazy(() =>
  import('@/containers/Teams/TeamSettings/Communication/CommunicationOverview'),
);
const Finance = lazy(() => import('@/containers/Cases/FinancialManagement/Finance'));
const FinanceSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Finance/FinanceSettings'),
);
const IntegrationSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Integrations/IntegrationSettings'),
);
const UserSettings = lazy(() => import('@/containers/UserSettings/UserSettings'));

const Repair = lazy(() => import('@/containers/Teams/TeamSettings/Repair/Repair'));
const TagsOverview = lazy(() => import('@/containers/Teams/TeamSettings/Tags/TagsOverview'));
const TaskTypesOverview = lazy(() =>
  import('@/containers/Teams/TeamSettings/TaskTypes/TaskTypesOverview'),
);
const TeamUsers = lazy(() => import('@/containers/Teams/TeamSettings/Users/TeamUsers'));

const NotFound = lazy(() => import('../../Error/NotFound'));

const Calendar = lazy(() => import('../../Calendars/Calendar'));
const MailboxCard = lazy(() => import('../../Mailbox/MailboxCard'));
const EmailAccounts = lazy(() => import('../../EmailAccounts/EmailAccounts'));

const ContactsOverview = lazy(() => import('../../Contacts/ContactsOverview'));
const CompaniesOverview = lazy(() => import('../../Companies/CompaniesOverview'));
const BuildingsOverview = lazy(() => import('../../Buildings/BuildingsOverview'));
const TasksOverview = lazy(() => import('../../Tasks/TasksOverview'));
const PoliciesOverview = lazy(() => import('../../Policies/PoliciesOverview'));
const CaseDetail = lazy(() => import('@/containers/Cases/CaseDetail'));
const CaseOverview = lazy(() => import('../../Cases/CaseOverview'));
const BatchCommunicationOverview = lazy(() =>
  import('../../BatchCommunication/BatchCommunicationOverview'),
);
const Insights = lazy(() => import('../../Insights/Insights'));

const DebtorOverviewWrapper = lazy(() =>
  import('../../Finance/Debtors/repair/DebtorOverviewWrapper'),
);
const EnvelopeOverview = lazy(() => import('../../Finance/Envelopes/EnvelopeOverview'));
const HoaDebtorOverview = lazy(() => import('../../Finance/Debtors/hoa/HoaDebtorOverview'));
const HOAInvoicesOverview = lazy(() => import('../../Finance/Invoices/hoa/InvoiceOverview'));
const RepairAcceptedInvoicesOverview = lazy(() =>
  import('@/containers/Finance/Invoices/repair/AcceptedInvoices'),
);
const RepairInvoicesOverview = lazy(() =>
  import('@/containers/Finance/Invoices/repair/RepairInvoicesOverview'),
);
const RepairRejectedInvoicesOverview = lazy(() =>
  import('@/containers/Finance/Invoices/repair/RejectedInvoices'),
);
const RepairToValidateInvoicesOverview = lazy(() =>
  import('@/containers/Finance/Invoices/repair/ToValidateInvoices'),
);
const SpreadsheetDetails = lazy(() =>
  import('@/containers/Finance/Spreadsheet/SpreadsheetDetails'),
);

const StatementsOverview = lazy(() => import('@/containers/Finance/Statements/StatementsOverview'));

const Policy = lazy(() => import('../../Policies/Policy'));

const Administration = lazy(() => import('../../Administration/Administration'));
const Support = lazy(() => import('../../Support/Support'));
const TeamSettings = lazy(() => import('../../Teams/TeamSettings/TeamSettings'));
const UserSettingsNav = lazy(() => import('../../UserSettings/UserSettingsNav'));

const BuildingDetail = lazy(() => import('../../Buildings/Details/BuildingDetail'));
const BuildingUnitDetails = lazy(() => import('../../Buildings/Units/Details/BuildingUnitDetails'));
const Contact = lazy(() => import('../../Contacts/Details/Contact'));
const Company = lazy(() => import('../../Companies/Details/Company'));
const BatchDetail = lazy(() => import('../../BatchCommunication/Details/Batch'));
const Case = lazy(() => import('../../Cases/Case'));
const ContractDetail = lazy(() =>
  import('../../Companies/Contracts/ContractDetail/ContractDetail'),
);

const HomeownerAssociationDetail = lazy(() =>
  import('../../HomeownerAssociation/HomeownerAssociationDetail'),
);
const HomeownerAssociationBuildings = lazy(() =>
  import('../../HomeownerAssociation/Buildings/HomeownerAssociationBuildings'),
);

const HomeownerAssociationSettings = lazy(() =>
  import('@/containers/HomeownerAssociation/Settings/HomeOwnerAssociationSettings'),
);
const HomeownerAssociationMeetings = lazy(() =>
  import('@/containers/HomeownerAssociation/Meetings/HomeownerAssociationMeetings'),
);
const HomeownerAssociationProvisions = lazy(() =>
  import('@/containers/HomeownerAssociation/Provisions/HomeownerAssociationProvisions'),
);
const MeterReadings = lazy(() =>
  import('@/containers/HomeownerAssociation/MeterReadings/HomeownerAssociationMeterReadings'),
);
const DirectDebit = lazy(() => import('@/containers/HomeownerAssociation/DirectDebit/DirectDebit'));
const HomeownerAssociationFinance = lazy(() =>
  import('@/containers/HomeownerAssociation/Finance/HomeownerAssociationFinance'),
);
const Budgets = lazy(() => import('@/containers/HomeownerAssociation/Budgets/Budgets'));
const HomeownerAssociationDocuments = lazy(() =>
  import('@/containers/HomeownerAssociation/Documents/HomeownerAssociationDocuments'),
);

const HomeownerAssociationCommunicationOverview = lazy(() =>
  import(
    '@/containers/HomeownerAssociation/Communication/Overview/HomeownerAssociationCommunicationOverview'
  ),
);
const HomeownerAssociationSyndicManagement = lazy(() =>
  import('@/containers/HomeownerAssociation/SyndicManagement/HomeownerAssociationSyndicManagement'),
);

const MeetingDetail = lazy(() =>
  import('@/containers/HomeownerAssociation/Meetings/Details/MeetingDetail'),
);
const MeetingInProgress = lazy(() =>
  import('@/containers/HomeownerAssociation/Meetings/InProgress/MeetingInProgress'),
);
const MeetingRegistration = lazy(() =>
  import('@/containers/HomeownerAssociation/Meetings/Registration/MeetingRegistration'),
);
const MeetingDetailRerouter = lazy(() => import('@/containers/Meetings/MeetingDetailRerouter'));

const MeetingPlanning = lazy(() => import('@/containers/Meetings/MeetingPlanning'));

const MeetingSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Meetings/MeetingSettings'),
);
const MeetingTypeDetail = lazy(() =>
  import('@/containers/Teams/TeamSettings/Meetings/MeetingTypes/MeetingTypeDetail'),
);
const InvoiceValidate = lazy(() =>
  import('@/containers/Finance/Invoices/hoa/toValidate/InvoiceValidate'),
);
const PerformanceSettings = lazy(() =>
  import('@/containers/Teams/TeamSettings/Performances/PerformanceSettings'),
);

const AssetTemplates = lazy(() =>
  import('@/containers/Teams/TeamSettings/Building/components/AssetTemplates'),
);

const HomeScores = lazy(() => import('@/containers/Administration/HomeScores'));

const HomeownerAssociationDashboard = lazy(() =>
  import('@/containers/HomeownerAssociation/Dashboard/HomeownerAssociationDashboard'),
);

const AccessKeysOverview = lazy(() => import('@/containers/AccessKeys/AccessKeysOverview'));
const AccessKeyDetail = lazy(() => import('@/containers/AccessKeys/details/AccessKeyDetail'));

const Router = () => {
  const { isAdmin, isSupervisor } = useUserRole();
  const { currentTeam } = useContext(AppContext);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const viewport = useViewport();

  const isRouteCallBack = useMemo(() => pathname === ROUTES.CALLBACK, [pathname]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isRouteCallBack || !currentTeam) {
      return;
    }
    // TODO: 8983: Performance: Refactor remaining redux store initial data fetching to AppContext
    dispatch(getUserTeamSettings());
    dispatch(getTags());
    dispatch(getPolicies());
    dispatch(getFsmaNumbers());
    dispatch(getTeamIntegrations());
  }, [dispatch, currentTeam, isRouteCallBack]);

  return (
    <Suspense
      fallback={
        <div
          style={{ height: viewport.mainContainerHeight }}
          className="animate-pulse rounded-3xl bg-background"
        />
      }
    >
      {isRouteCallBack && (
        <Routes>
          <Route path={ROUTES.CALLBACK} element={<CallbackFront />} />
        </Routes>
      )}

      <Routes>
        {isAdmin && (
          <Route
            path={ROUTES.ADMINISTRATION}
            element={
              <>
                <Meta>
                  <title>{t('administration')}</title>
                </Meta>
                <Administration />
              </>
            }
          >
            <Route path={ROUTES.ADMINISTRATION} element={<BlogOverview />} />
            <Route path={ROUTES.ADMINISTRATION_BLOG} element={<BlogOverview />} />
            <Route path={ROUTES.ADMINISTRATION_FSMA} element={<FsmaNbrOverview />} />
            <Route path={ROUTES.ADMINISTRATION_INTEGRATORS} element={<IntegratorsOverview />} />
            <Route path={ROUTES.ADMINISTRATION_SMS} element={<SmsOverview />} />
            <Route path={ROUTES.ADMINISTRATION_KBO} element={<KboPublicSearchBalance />} />
            <Route
              path={ROUTES.ADMINISTRATION_UNUSED_OCTOPUS_DOSSIERS}
              element={<UnusedOctopusDossiers />}
            />
            <Route path={ROUTES.ADMINISTRATION_HOME_SCORE} element={<HomeScores />} />
          </Route>
        )}

        {currentTeam?.allowRepairManagement && (
          <Route
            element={
              <>
                <Meta>
                  <title>{t('invoices')}</title>
                </Meta>
                <RepairInvoicesOverview />
              </>
            }
            path={ROUTES.REPAIR_INVOICE_MANAGEMENT}
          >
            <Route
              element={<RepairAcceptedInvoicesOverview />}
              path={ROUTES.REPAIR_INVOICES_ACCEPTED}
            />
            <Route
              element={<RepairRejectedInvoicesOverview />}
              path={ROUTES.REPAIR_INVOICES_REJECTED}
            />
            <Route
              element={<RepairToValidateInvoicesOverview />}
              path={ROUTES.REPAIR_INVOICES_TO_VALIDATE}
            />
          </Route>
        )}
        {(isSupervisor || isAdmin) && currentTeam?.allowRepairManagement && (
          <Route
            path={ROUTES.REPAIR_DEBTOR_MANAGEMENT}
            element={
              <>
                <Meta>
                  <title>{t('debtors')}</title>
                </Meta>
                <DebtorOverviewWrapper />
              </>
            }
          />
        )}
        {isSupervisor && (
          <Route
            path={ROUTES.TEAM_SETTINGS}
            element={
              <>
                <Meta>
                  <title>{t('teamSettings')}</title>
                </Meta>
                <TeamSettings />
              </>
            }
          >
            {!!currentTeam?.allowRepairManagement && (
              <Route path={ROUTES.TEAM_SETTINGS_REPAIR} element={<Repair />} />
            )}
            {!!currentTeam?.allowRepairManagement && (
              <Route
                path={ROUTES.TEAM_SETTINGS_AUTOMATIONS}
                element={
                  <Suspense fallback={<InlineSpinner />}>
                    <AutomationSettings />
                  </Suspense>
                }
              />
            )}
            {!!currentTeam?.allowAiUsage && (
              <Route
                path={ROUTES.TEAM_SETTINGS_AI_EMAIL_DRAFTING}
                element={
                  <Suspense fallback={<InlineSpinner />}>
                    <AiEmailDraftingSettings />
                  </Suspense>
                }
              />
            )}
            {!!currentTeam?.allowSyndicManagement && (
              <>
                <Route path={ROUTES.TEAM_SETTINGS_ASSETS} element={<AssetTemplates />} />

                {isSupervisor && (
                  <>
                    <Route path={ROUTES.TEAM_SETTINGS_MEETINGS} element={<MeetingSettings />} />
                    <Route
                      path={`${ROUTES.TEAM_SETTINGS_MEETINGS}/${ROUTES.TEAM_SETTINGS_MEETINGS_MEETINGTYPE_DETAIL}`}
                      element={<MeetingTypeDetail />}
                    />
                    <Route
                      path={ROUTES.TEAM_SETTINGS_PERFORMANCES}
                      element={<PerformanceSettings />}
                    />
                  </>
                )}
              </>
            )}
            {(!!currentTeam?.allowSyndicManagement || !!currentTeam?.allowRepairManagement) && (
              <Route
                path={ROUTES.TEAM_SETTINGS_FINANCE}
                element={
                  <Suspense fallback={<InlineSpinner />}>
                    <FinanceSettings />
                  </Suspense>
                }
              />
            )}
            <Route
              path={ROUTES.TEAM_SETTINGS_BUILDING_PORTAL}
              element={<BuildingPortalSettings />}
            />

            <Route
              path={ROUTES.TEAM_SETTINGS_CASE}
              element={
                <FileContextProvider type={FileContextParentType.CaseTemplate}>
                  <CaseSettings />
                </FileContextProvider>
              }
            />
            <Route path={ROUTES.TEAM_SETTINGS_COMMUNICATION} element={<CommunicationOverview />} />
            <Route path={ROUTES.TEAM_SETTINGS_GENERAL} element={<CommonTeamSettings />} />
            <Route path={ROUTES.TEAM_SETTINGS_INTEGRATIONS} element={<IntegrationSettings />} />
            <Route path={ROUTES.TEAM_SETTINGS_LABELS} element={<TagsOverview />} />
            <Route path={ROUTES.TEAM_SETTINGS_TASKTYPES} element={<TaskTypesOverview />} />
            <Route path={ROUTES.TEAM_SETTINGS_TEAMMEMBERS} element={<TeamUsers />} />
          </Route>
        )}
        {currentTeam?.allowSyndicManagement && (
          <>
            <Route
              path={ROUTES.HOA_DEBTOR_MANAGEMENT}
              element={
                <>
                  <Meta>
                    <title>{t('debtors')}</title>
                  </Meta>
                  <HoaDebtorOverview />
                </>
              }
            />
            <Route
              path={ROUTES.HOA_INVOICE_MANAGEMENT}
              element={
                <>
                  <Meta>
                    <title>{t('invoices')}</title>
                  </Meta>
                  <HOAInvoicesOverview />
                </>
              }
            />
            <Route
              path={ROUTES.ACCOUNTING_STATEMENTS}
              element={
                <>
                  <Meta>
                    <title>{t('statements')}</title>
                  </Meta>
                  <StatementsOverview />
                </>
              }
            />
            <Route
              path={ROUTES.ACCOUNTING_ENVELOPES}
              element={
                <>
                  <Meta>
                    <title>{t('payments')}</title>
                  </Meta>
                  <EnvelopeOverview />
                </>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.HOME}
          element={
            <>
              <Meta>
                <title>{t('dashboard.label')}</title>
              </Meta>
              <Dashboard />
            </>
          }
        />
        <Route
          path={ROUTES.COMPANIES_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('companies')}</title>
              </Meta>
              <Suspense fallback={<OverViewSkeleton />}>
                <CompaniesOverview />
              </Suspense>
            </>
          }
        />
        <Route
          path={ROUTES.BUILDINGS_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('building_other')}</title>
              </Meta>
              <Suspense fallback={<OverViewSkeleton />}>
                <BuildingsOverview />
              </Suspense>
            </>
          }
        />
        <Route
          path={ROUTES.CASE_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('case_other')}</title>
              </Meta>
              <Suspense fallback={<OverViewSkeleton />}>
                <CaseOverview />
              </Suspense>
            </>
          }
        />
        <Route
          path={ROUTES.TASKS_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('tasks')}</title>
              </Meta>
              <Suspense fallback={<OverViewSkeleton />}>
                <TasksOverview />
              </Suspense>
            </>
          }
        />
        <Route
          path={ROUTES.CONTACT_DASHBOARD}
          element={
            <>
              <Meta>
                <title>{t('contacts')}</title>
              </Meta>
              <Suspense fallback={<OverViewSkeleton />}>
                <ContactsOverview />
              </Suspense>
            </>
          }
        />

        <Route path={ROUTES.CONTACT_DETAIL} element={<Contact />}>
          <Route index element={<ContactDetails />} />
          <Route path={ROUTES.DETAILS_MAILBOX} element={<ContactCommunicationOverview />} />
        </Route>

        <Route path={ROUTES.COMPANY_DETAIL} element={<Company />}>
          <Route index element={<CompanyDetails />} />
          <Route path={ROUTES.DETAILS_MAILBOX} element={<CompanyCommunicationOverview />} />
        </Route>

        <Route
          path={ROUTES.SUPPORT_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('support')}</title>
              </Meta>
              <Support />
            </>
          }
        />
        <Route
          path={ROUTES.USER_SETTINGS}
          element={
            <>
              <Meta>
                <title>{t('userSettings')}</title>
              </Meta>
              <UserSettingsNav />
            </>
          }
        >
          <Route path={ROUTES.USER_SETTINGS_GENERAL} element={<UserSettings />} />
          <Route path={ROUTES.USER_SETTINGS_CALENDARS} element={<CalendarsOverview isPersonal />} />
          <Route path={ROUTES.USER_SETTINGS_EMAILACCOUNTS} element={<EmailAccounts />} />
        </Route>

        <Route
          path={ROUTES.MAILBOX}
          element={
            <>
              <Meta>
                <title>{t('mailbox')}</title>
              </Meta>
              <MailboxCard />
            </>
          }
        />
        <Route
          path={ROUTES.CALENDAR}
          element={
            <>
              <Meta>
                <title>{t('calendar')}</title>
              </Meta>
              <Calendar />
            </>
          }
        />
        <Route path={ROUTES.CONTRACT_DETAIL} element={<ContractDetail />} />
        <Route
          path={ROUTES.INSIGHTS}
          element={
            <>
              <Meta>
                <title>{t('insights.title')}</title>
              </Meta>
              <Insights />
            </>
          }
        />
        <Route path={ROUTES.BUILDING_DETAIL}>
          <Route
            index
            element={
              <BuildingContextProvider>
                <BuildingDetail />
              </BuildingContextProvider>
            }
          />
          <Route
            path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL}
            element={
              <HomeownerAssociationContextProvider>
                <HomeownerAssociationDetail />
              </HomeownerAssociationContextProvider>
            }
          >
            <Route index element={<HomeownerAssociationDashboard />} />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_SETTINGS}
              element={<HomeownerAssociationSettings />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_MEETINGS}
              element={<HomeownerAssociationMeetings />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_ACCOUNTING}
              element={<HomeownerAssociationFinance />}
            />
            <Route path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_COSTS} />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_BUDGET}
              element={<Budgets />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_PROVISIONS}
              element={<HomeownerAssociationProvisions />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_DOCUMENTS}
              element={<HomeownerAssociationDocuments />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_COMMUNICATION}
              element={<HomeownerAssociationCommunicationOverview />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_SYNDICMANAGEMENT}
              element={<HomeownerAssociationSyndicManagement />}
            />
            <Route
              path={ROUTES.BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_METERREADINGS}
              element={<MeterReadings />}
            />
            <Route
              path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_DIRECT_DEBIT}
              element={<DirectDebit />}
            />
            <Route
              path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_INVOICES}
              element={<HOAInvoicesOverview />}
            />
            <Route
              path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_RECONCILIATIONS}
              element={<StatementsOverview />}
            />
            <Route
              path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_DEBTORS}
              element={<HoaDebtorOverview />}
            />
            <Route
              path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_ENVELOPES}
              element={<EnvelopeOverview />}
            />
            <Route path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_BUILDINGS}>
              <Route index element={<HomeownerAssociationBuildings />} />
              <Route path=":buildingId" element={<HomeownerAssociationBuildings />} />
            </Route>
          </Route>
        </Route>
        <Route
          path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL}
          element={
            <HomeownerAssociationContextProvider>
              <HomeownerAssociationDetail />
            </HomeownerAssociationContextProvider>
          }
        >
          <Route index element={<HomeownerAssociationDashboard />} />

          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_SETTINGS}
            element={<HomeownerAssociationSettings />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_MEETINGS}
            element={<HomeownerAssociationMeetings />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_ACCOUNTING}
            element={<HomeownerAssociationFinance />}
          />
          <Route path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_COSTS} />
          <Route path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_BUDGET} element={<Budgets />} />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_PROVISIONS}
            element={<HomeownerAssociationProvisions />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_DOCUMENTS}
            element={<HomeownerAssociationDocuments />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_COMMUNICATION}
            element={<HomeownerAssociationCommunicationOverview />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_SYNDICMANAGEMENT}
            element={<HomeownerAssociationSyndicManagement />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_METERREADINGS}
            element={<MeterReadings />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_DIRECT_DEBIT}
            element={<DirectDebit />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_INVOICES}
            element={<HOAInvoicesOverview />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_RECONCILIATIONS}
            element={<StatementsOverview />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_DEBTORS}
            element={<HoaDebtorOverview />}
          />
          <Route
            path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_ENVELOPES}
            element={<EnvelopeOverview />}
          />
          <Route path={ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_BUILDINGS}>
            <Route index element={<HomeownerAssociationBuildings />} />
            <Route path=":buildingId" element={<HomeownerAssociationBuildings />} />
          </Route>
        </Route>
        <Route
          path={ROUTES.BUILDING_UNIT_DETAIL}
          element={
            <BuildingContextProvider>
              <BuildingUnitDetails />
            </BuildingContextProvider>
          }
        />
        <Route path={ROUTES.HOMEOWNER_ASSOCIATION_MEETING_DETAIL} element={<MeetingDetail />} />
        <Route path={ROUTES.MEETING_DETAIL} element={<MeetingDetailRerouter />} />
        <Route path={ROUTES.MEETING_IN_PROGRESS} element={<MeetingInProgress />} />
        <Route path={ROUTES.MEETING_REGISTRATION} element={<MeetingRegistration />} />
        <Route
          path={ROUTES.MEETINGS_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('meetings')}</title>
              </Meta>
              <MeetingPlanning />
            </>
          }
        />
        <Route
          path={ROUTES.CASE_DETAIL}
          element={
            <FileContextProvider type={FileContextParentType.Case}>
              <Case />
            </FileContextProvider>
          }
        >
          <Route path={ROUTES.CASE_DETAIL} element={<CaseDetail />} />
          <Route path={ROUTES.CASE_FINANCE} element={<Finance />} />
          <Route path={ROUTES.CASE_MAILBOX} element={<CaseEmailsOverview />} />
        </Route>
        <Route path={ROUTES.FINANCE} element={<Finance />} />
        <Route
          element={
            <FileContextProvider type={FileContextParentType.Spreadsheet}>
              <SpreadsheetDetails />
            </FileContextProvider>
          }
          path={ROUTES.SPREADSHEET_DETAIL}
        />
        <Route
          path={ROUTES.TEAM_SETTINGS_EMAILACCOUNT_DETAIL}
          element={<EmailAccounts showTeamMailboxes />}
        />
        <Route
          path={ROUTES.POLICY_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('policies')}</title>
              </Meta>
              <PoliciesOverview />
            </>
          }
        />
        <Route
          path={ROUTES.POLICY_DETAIL}
          element={
            <FileContextProvider type={FileContextParentType.Policy}>
              <Policy />
            </FileContextProvider>
          }
        />
        <Route
          path={ROUTES.BATCH_COMMUNICATION_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('mailings')}</title>
              </Meta>
              <BatchCommunicationOverview />
            </>
          }
        />
        <Route
          path={ROUTES.BATCH_COMMUNICATION_COMBINED_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('mailings')}</title>
              </Meta>
              <BatchCommunicationOverview />
            </>
          }
        />
        <Route path={ROUTES.BATCH_COMMUNICATION_DETAIL} element={<BatchDetail />} />
        <Route path={ROUTES.HOA_INVOICE_VALIDATE} element={<InvoiceValidate />} />
        <Route path={ROUTES.HOA_INVOICE_UPLOAD} element={<InvoiceValidate />} />
        <Route path="*" element={<NotFound />} />
        <Route
          path={ROUTES.SYNDIC_MANAGMENT_OVERVIEW}
          element={
            <>
              <Meta>
                <title>{t('_syndicManagement.salesInvoices.title')}</title>
              </Meta>
              <SyndicManagementOverview />
            </>
          }
        />
        {currentTeam?.allowAccessKeyManagement && (
          <>
            <Route path={ROUTES.ACCESS_KEYS} element={<AccessKeysOverview />} />
            <Route path={ROUTES.ACCESS_KEYS_LENDINGS} element={<AccessKeysOverview />} />
            <Route path={ROUTES.ACCESS_KEY_DETAILS} element={<AccessKeyDetail />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
};

export default Router;
